import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FAQHero from './components/FAQHero';
import FAQSection from './components/FAQSection';
import FAQCTASection from './components/FAQCTASection';
import { useSEO } from '../../utils/seo';

export default function FAQPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://brandcapital.it';
  
  useSEO({
    title: 'FAQ - Domande Frequenti Valutazione Brand e Perizie Certificate',
    description: 'Risposte alle domande più frequenti sulla valutazione del brand, perizie certificate, costi, tempi e benefici. Scopri tutto quello che devi sapere sulla valorizzazione del brand aziendale.',
    keywords: 'faq valutazione brand, domande perizia marchio, costi valutazione, tempi certificazione brand',
    canonical: `${siteUrl}/faq`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Cos\'è esattamente il Brand Capital e perché dovrebbe interessarmi?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Il Brand Capital è il valore economico del tuo brand, misurato e certificato secondo standard internazionali. Non è solo un numero: è la trasformazione dei tuoi investimenti in marketing da costo a patrimonio aziendale. Significa che ogni euro speso per costruire il tuo brand diventa un attivo che aumenta il valore della tua azienda, migliora il bilancio e ti apre nuove opportunità di crescita.'
          }
        },
        {
          '@type': 'Question',
          name: 'La mia azienda è piccola, ha senso valorizzare il brand?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Assolutamente sì. Anzi, per le PMI è ancora più strategico. Un brand valorizzato ti permette di competere con aziende più grandi, di ottenere condizioni migliori dalle banche, di attrarre partner e investitori. Non serve essere una multinazionale: serve avere un brand riconoscibile e una strategia chiara. Noi lavoriamo proprio con PMI che vogliono fare il salto di qualità.'
          }
        },
        {
          '@type': 'Question',
          name: 'Quanto costa valorizzare il brand?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dipende dalle dimensioni della tua azienda e dagli obiettivi. Ma la domanda giusta è: quanto ti costa NON valorizzarlo? Ogni anno spendi in marketing senza capitalizzare nulla. Con la valorizzazione, quegli stessi investimenti diventano patrimonio. Offriamo una pre-analisi gratuita per capire insieme il potenziale del tuo brand e darti un\'idea chiara dei costi e dei benefici.'
          }
        },
        {
          '@type': 'Question',
          name: 'Quanto tempo ci vuole per completare la valorizzazione?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Il processo completo richiede mediamente 60-90 giorni, dalla raccolta dati alla certificazione finale. Ma i benefici iniziano subito: già dalla pre-analisi avrai una visione chiara del valore del tuo brand e potrai iniziare a pianificare le strategie di crescita. Non è un processo burocratico: è un percorso strategico che ti accompagniamo passo dopo passo.'
          }
        },
        {
          '@type': 'Question',
          name: 'Cosa succede dopo la valorizzazione? Devo fare qualcosa?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Dopo la certificazione, il valore del brand entra nel tuo bilancio come attivo immateriale. Da quel momento puoi usarlo per migliorare i rapporti con le banche, attrarre investitori, strutturare royalty, pianificare operazioni straordinarie. Ti forniamo anche un piano di governance per mantenere e far crescere il valore nel tempo. Non ti lasciamo solo: ti affianchiamo nella fase di utilizzo strategico.'
          }
        },
        {
          '@type': 'Question',
          name: 'Il Brand Capital migliora davvero l\'accesso al credito?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sì, in modo concreto. Un brand valorizzato aumenta il patrimonio netto della tua azienda, migliorando tutti gli indici di bilancio che le banche guardano. Inoltre, dimostra che hai un asset strategico che genera valore nel tempo. Molti nostri clienti hanno ottenuto finanziamenti più alti e a condizioni migliori proprio grazie alla valorizzazione del brand.'
          }
        },
        {
          '@type': 'Question',
          name: 'Posso valorizzare il brand anche se non ho un bilancio certificato?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sì, la valorizzazione del brand è possibile anche per aziende in regime semplificato. Quello che conta è avere un brand riconoscibile, una presenza sul mercato e dati misurabili (fatturato, clienti, investimenti marketing). La certificazione segue standard internazionali che si adattano a diverse tipologie di impresa. Nella pre-analisi valutiamo insieme la fattibilità.'
          }
        },
        {
          '@type': 'Question',
          name: 'Cosa significa "royalty sul brand" e come funziona?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'È un sistema che ti permette di generare reddito ricorrente dal tuo brand. In pratica, costituisci una holding personale che detiene il marchio e lo concede in licenza alla tua azienda operativa, che paga una royalty periodica. Questo ti permette di ottimizzare la fiscalità, proteggere il brand e creare un flusso di reddito stabile e prevedibile. È una strategia usata dalle grandi aziende, ora accessibile anche alle PMI.'
          }
        },
        {
          '@type': 'Question',
          name: 'La valorizzazione del brand è riconosciuta fiscalmente?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Sì, se fatta secondo gli standard internazionali (ISO 10668) e le normative contabili (IAS 38/IFRS). Il valore del brand può essere iscritto a bilancio come attivo immateriale, con tutti i benefici fiscali e patrimoniali che ne derivano. Lavoriamo sempre con commercialisti e revisori per garantire la piena conformità normativa.'
          }
        },
        {
          '@type': 'Question',
          name: 'Perché dovrei scegliere voi invece di fare tutto internamente?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'La valorizzazione del brand richiede competenze specifiche: analisi finanziaria, marketing strategico, normative internazionali, perizie certificate. Fare tutto internamente significa investire tempo, risorse e rischiare errori costosi. Noi abbiamo un metodo collaudato, certificazioni riconosciute e un team di esperti che ti accompagna in ogni fase. In più, offriamo la pre-analisi gratuita: zero rischi, massima chiarezza.'
          }
        },
        {
          '@type': 'Question',
          name: 'Cosa include esattamente la pre-analisi gratuita?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'La pre-analisi è un check-up completo del tuo brand. Analizziamo la tua presenza sul mercato, gli investimenti in marketing, il posizionamento competitivo e ti forniamo una stima preliminare del valore del tuo brand. Ricevi un report chiaro con i potenziali benefici economici, i tempi e i passi successivi. È gratuita, senza impegno e ti dà una visione strategica immediata.'
          }
        },
        {
          '@type': 'Question',
          name: 'Il valore del brand può diminuire nel tempo?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Come ogni asset, il brand va gestito e protetto. Se smetti di investire, se la qualità cala o se perdi rilevanza sul mercato, il valore può diminuire. Per questo ti forniamo un piano di governance: strategie concrete per mantenere e far crescere il valore nel tempo. Non è un certificato da mettere nel cassetto: è uno strumento vivo che richiede attenzione strategica.'
          }
        }
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <FAQHero />
      <FAQSection />
      <FAQCTASection />
      <Footer />
    </div>
  );
}
